"use client";

import { FC, useCallback, useEffect } from "react";
import { Vehicle } from "../../../types/vehicle";
import { BaseAction } from "../../../types/site";
import { handleButtonAction } from "../../../utils/ui";
import { useState } from "react";
import {
  ButtonInfo,
  MESSAGE_TYPE_BUTTON_DATA,
  MESSAGE_TYPE_GET_BUTTON_INFO,
  MESSAGE_TYPE_SET_FINAL_PRICE,
  MESSAGE_TYPE_SHOW_BUTTON,
  MESSAGE_TYPE_UPDATE_BUTTON,
} from "../../../utils/extentionContants";
import { processActionButtonVariables } from "../../../utils/actionButtons";
import { getButtonActionResult } from "../../../utils/analitics";
import { WebWorkerActionBututton } from "../../../types/action-buttons";
import useAnalytics from "@/analytics/useAnalytics";

interface IActionButton {
  actionButton: WebWorkerActionBututton;
  currentVehicle: Vehicle;
  prfetchedData: any;
  className?: string;
  vehicleDataChanged: () => void;
}

const ActionButtonCommon: FC<IActionButton> = ({
  actionButton,
  currentVehicle,
  prfetchedData,
  className,
  vehicleDataChanged,
}) => {
  const { sendAnalyticsEvent } = useAnalytics();
  const [buttonVisible, setButtonVisible] = useState<boolean>(!actionButton.dynamic);
  const [layout, setLayout] = useState(
    processActionButtonVariables(actionButton.layout, actionButton.actionButtonData, currentVehicle),
  );
  const [gotInitialValues, setGotInitialValues] = useState(false);

  const contextId = currentVehicle.id;

  const getButtonContext = useCallback(() => {
    const workerContextData = actionButton.worker?.contextData;
    const { ...vehicleData } = currentVehicle;
    return {
      ...workerContextData!,
      vehicle: vehicleData,
      prefetchedData: prfetchedData,
      contextId,
      visible: buttonVisible,
    };
  }, [actionButton.worker?.contextData, buttonVisible, contextId, currentVehicle, prfetchedData]);

  const clickHandler = useCallback(() => {
    if (actionButton.dynamic && actionButton.worker) {
      actionButton.worker?.postClickMessage(getButtonContext());
    } else {
      const buttonForHandling: BaseAction = {
        routeType: actionButton.routeType,
        width: actionButton.popupData?.width,
        height: actionButton.popupData?.height,
        allowScrolling: actionButton.popupData?.allowScrolling,
        closeButtonType: actionButton.popupData?.closeButtonType,
        allowFullScreen: actionButton.popupData?.allowFullScreen,
        mobileWidth: actionButton.popupData?.mobileWidth,
        mobileHeight: actionButton.popupData?.mobileHeight,
        maxWidth: actionButton.popupData?.maxWidth,
        maxHeight: actionButton.popupData?.maxHeight,
      };
      if (actionButton.path) {
        buttonForHandling.path = processActionButtonVariables(
          actionButton.path,
          actionButton.actionButtonData,
          currentVehicle,
        );
      }
      handleButtonAction(buttonForHandling, {
        vehicleId: currentVehicle.id,
      });
      const buttonTitle = actionButton.actionButtonData.caption || actionButton.buttonName;
      sendAnalyticsEvent("asc_cta_interaction", {
        element_text: buttonTitle,
        element_title: actionButton.buttonName,
        element_type: "digital_retailing_tool",
        event_action_result: getButtonActionResult(buttonForHandling),
        link_url: buttonForHandling.path,
      });
    }
  }, [
    actionButton.actionButtonData,
    actionButton.buttonName,
    actionButton.dynamic,
    actionButton.path,
    actionButton.popupData?.allowFullScreen,
    actionButton.popupData?.allowScrolling,
    actionButton.popupData?.closeButtonType,
    actionButton.popupData?.height,
    actionButton.popupData?.maxHeight,
    actionButton.popupData?.maxWidth,
    actionButton.popupData?.mobileHeight,
    actionButton.popupData?.mobileWidth,
    actionButton.popupData?.width,
    actionButton.routeType,
    actionButton.worker,
    currentVehicle,
    getButtonContext,
    sendAnalyticsEvent,
  ]);

  useEffect(() => {
    if (actionButton.worker) {
      actionButton.worker.setListener(contextId, (message: any) => {
        if (message) {
          if (message.type === MESSAGE_TYPE_BUTTON_DATA) {
            const buttonInfo: ButtonInfo = message.buttonInfo;
            if (buttonInfo.visible !== undefined) {
              setButtonVisible(buttonInfo.visible);
            }
            const newlayout = processActionButtonVariables(actionButton.layout, buttonInfo.params, currentVehicle);
            setLayout(newlayout);
          } else if (message.type === MESSAGE_TYPE_UPDATE_BUTTON) {
            const contextData = getButtonContext();
            actionButton.worker!.postMessage({ type: MESSAGE_TYPE_GET_BUTTON_INFO, context: contextData });
          } else if (message.type === MESSAGE_TYPE_SET_FINAL_PRICE) {
            if (message.price) {
              currentVehicle.finalPrice = message.price;
              if (vehicleDataChanged) {
                vehicleDataChanged();
              }
            }
          } else if (message.type === MESSAGE_TYPE_SHOW_BUTTON) {
            setButtonVisible(message.show);
          }
        }
      });
      if (!gotInitialValues) {
        setGotInitialValues(true);
        const contextData = getButtonContext();
        actionButton.worker.postMessage({ type: MESSAGE_TYPE_GET_BUTTON_INFO, context: contextData });
      }
    }

    return () => {
      if (actionButton.worker) {
        actionButton.worker.deleteListener(contextId);
      }
    };
  }, [
    actionButton.layout,
    actionButton.worker,
    contextId,
    currentVehicle,
    getButtonContext,
    gotInitialValues,
    prfetchedData,
    vehicleDataChanged,
  ]);

  return buttonVisible ? (
    <div onClick={clickHandler} className={className} dangerouslySetInnerHTML={{ __html: layout }}></div>
  ) : null;
};

export default ActionButtonCommon;
